import { Outlet, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { Button, AvatarComponent } from "@learn-me/ui";
import {
  LayoutDashboard,
  GraduationCap,
  Users,
  CreditCard,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Layers,
  Package,
  TrendingUp,
  BookOpen,
  Sparkles,
  Building2,
  UserCircle,
  FileText,
  Presentation,
} from "lucide-react";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useReducedMotion } from "@/hooks";
import { ANIMATION_CONFIG } from "@/lib/animations";
import { Logo } from "@/components/Logo";

const navigation = [
  { name: "Dashboard", href: "/admin", icon: LayoutDashboard },
  { name: "Cursos", href: "/admin/courses", icon: GraduationCap },
  { name: "Categorías", href: "/admin/categories", icon: Layers },
  { name: "Paquetes", href: "/admin/bundles", icon: Package },
  { name: "Materiales", href: "/admin/materials", icon: FileText },
  { name: "Programas", href: "/admin/programas", icon: Presentation },
  { name: "Estudiantes", href: "/admin/students", icon: Users },
  { name: "Pagos", href: "/admin/payments", icon: CreditCard },
  { name: "Finanzas", href: "/admin/finance", icon: TrendingUp },
  { name: "Tareas IA", href: "/admin/ai-tasks", icon: Sparkles },
  { name: "Empresas", href: "/admin/enterprise-contacts", icon: Building2 },
  { name: "Equipo", href: "/admin/team", icon: UserCircle },
];

export default function AdminLayout() {
  const { user, logout, logoutLoading } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 text-white transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-auto ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-800">
            <Link to="/admin" className="flex items-center">
              <Logo variant="white" className="h-10" />
            </Link>
            <button
              className="lg:hidden p-2"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Admin badge */}
          <div className="px-4 py-3 bg-primary/10 mx-4 mt-4 rounded-lg">
            <span className="text-xs font-medium text-primary">
              Panel de Administracion
            </span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-gray-400 hover:bg-gray-800 hover:text-white"
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-gray-800">
            <div className="flex items-center gap-3 mb-4">
              <AvatarComponent
                src={user?.avatarUrl}
                alt={user?.firstName || "Admin"}
                fallback={user?.firstName?.charAt(0) || "A"}
              />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-sm text-gray-400 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start gap-2 text-gray-400 hover:text-white hover:bg-gray-800"
              size="sm"
              onClick={() => logout()}
              disabled={logoutLoading}
            >
              <LogOut className="h-4 w-4" />
              Cerrar sesion
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={prefersReducedMotion ? {} : { opacity: 0 }}
            transition={{ duration: ANIMATION_CONFIG.duration.fast }}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen bg-gray-50">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b flex items-center justify-between px-4 lg:px-8">
          <button
            className="lg:hidden p-2 -ml-2"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex-1" />

          {/* User dropdown */}
          <div className="relative">
            <button
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
            >
              <AvatarComponent
                src={user?.avatarUrl}
                alt={user?.firstName || "Admin"}
                fallback={user?.firstName?.charAt(0) || "A"}
                size="sm"
              />
              <span className="hidden sm:block font-medium">
                {user?.firstName}
              </span>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </button>

            <AnimatePresence>
              {userMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <motion.div
                    initial={
                      prefersReducedMotion
                        ? {}
                        : { opacity: 0, scale: 0.95, y: -8 }
                    }
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={
                      prefersReducedMotion
                        ? {}
                        : { opacity: 0, scale: 0.95, y: -8 }
                    }
                    transition={{ duration: ANIMATION_CONFIG.duration.fast }}
                    className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border py-1 z-20"
                  >
                    <Link
                      to="/"
                      className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-muted"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <BookOpen className="h-4 w-4" />
                      Ver sitio
                    </Link>
                    <button
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-muted text-left"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Settings className="h-4 w-4" />
                      Configuracion
                    </button>
                    <hr className="my-1" />
                    <button
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-destructive hover:bg-muted text-left"
                      onClick={() => {
                        setUserMenuOpen(false);
                        logout();
                      }}
                      disabled={logoutLoading}
                    >
                      <LogOut className="h-4 w-4" />
                      Cerrar sesion
                    </button>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={prefersReducedMotion ? {} : { opacity: 0, y: -8 }}
              transition={{ duration: ANIMATION_CONFIG.duration.normal }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
